import { useEffect, useContext } from "react";
import { CartContext } from "@/contexts/CartContext";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./CartErrorBanner.module.css";

const AUTO_DISMISS_MS = 6000;

/**
 * Renders a top-of-page banner whenever CartContext has a non-null error.
 * 409 responses (cart-mode conflict / digital-needs-login) get a friendlier
 * translated message; other errors fall back to the raw status string.
 */
export function CartErrorBanner() {
  const ctx = useContext(CartContext);
  const texts = useLocalizedTexts();

  useEffect(() => {
    if (!ctx?.error) return;
    const id = setTimeout(() => ctx.clearError(), AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [ctx]);

  if (!ctx?.error) return null;

  const message = formatMessage(ctx.error, texts);

  return (
    <div role="alert" aria-live="polite" className={styles.banner}>
      <span className={styles.message}>{message}</span>
      <button
        type="button"
        className={styles.dismiss}
        onClick={ctx.clearError}
        aria-label={t(texts, "Tracker.Generic.Dismiss", "Dismiss")}
      >
        ×
      </button>
    </div>
  );
}

function formatMessage(error: string, texts: Record<string, string>): string {
  if (error.startsWith("409")) {
    return t(
      texts,
      "Tracker.Cart.ModeConflict",
      "Your cart already contains a different type of product. Remove those items first to add this one."
    );
  }
  if (error.startsWith("401")) {
    return t(
      texts,
      "Tracker.Cart.LoginRequired",
      "Please sign in to add subscriptions to your cart."
    );
  }
  return error;
}
